import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Spinner } from '../../components/UI/Spinner';
import {
  Coins,
  Plus,
  Edit2,
  Trash2,
} from 'lucide-react';

export const AdminCurrencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    symbol: '',
  });
  const [errors, setErrors] = useState({});

  const { success, error: toastError } = useToast();

  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.currencies();
      if (res && res.data) {
        setCurrencies(res.data);
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar monedas.');
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const openCreateModal = () => {
    setEditingCurrency(null);
    setFormData({ name: '', code: '', symbol: '' });
    setErrors({});
    setModalOpen(true);
  };

  const openEditModal = (c) => {
    setEditingCurrency(c);
    setFormData({
      name: c.name || '',
      code: c.code || '',
      symbol: c.symbol || '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      if (editingCurrency) {
        await adminService.updateCurrency(editingCurrency.id, formData);
        success('Moneda actualizada correctamente.');
      } else {
        await adminService.createCurrency(formData);
        success('Moneda agregada correctamente.');
      }
      setModalOpen(false);
      fetchCurrencies();
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al procesar la moneda.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currencyToDelete) return;
    setSubmitting(true);
    try {
      await adminService.deleteCurrency(currencyToDelete.id);
      success('Moneda eliminada correctamente.');
      setDeleteConfirmOpen(false);
      setCurrencyToDelete(null);
      fetchCurrencies();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar moneda.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Coins className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Configuración de Monedas
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Administra las divisas disponibles para la facturación y presupuestos
          </p>
        </div>

        <Button onClick={openCreateModal} icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
          Nueva Moneda
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando divisas..." className="py-12" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Nombre de la Divisa</th>
                    <th className="py-3 px-4">Código ISO</th>
                    <th className="py-3 px-4">Símbolo</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {currencies.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-slate-100">
                        {c.name}
                      </td>

                      <td className="py-3.5 px-4 font-mono font-bold text-brand-600 dark:text-brand-400">
                        {c.code}
                      </td>

                      <td className="py-3.5 px-4 font-mono font-bold text-slate-800 dark:text-slate-200">
                        {c.symbol}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Edit2}
                            onClick={() => openEditModal(c)}
                            title="Editar"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setCurrencyToDelete(c);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Eliminar"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title={editingCurrency ? 'Editar Moneda' : 'Nueva Moneda'}
        subtitle="Ingresa el nombre, código y símbolo de la divisa"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Nombre de la Moneda"
            placeholder="Dólar Estadounidense"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={errors.name?.[0]}
            required
            autoFocus
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Código ISO (3 caracteres)"
              placeholder="USD"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              error={errors.code?.[0]}
              required
            />

            <Input
              label="Símbolo"
              placeholder="$"
              value={formData.symbol}
              onChange={(e) => setFormData(prev => ({ ...prev, symbol: e.target.value }))}
              error={errors.symbol?.[0]}
              required
            />
          </div>

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={submitting} className="bg-[#08707A] hover:bg-[#065B63]">
              {editingCurrency ? 'Guardar Cambios' : 'Crear Moneda'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar moneda?"
        message={`¿Estás seguro de que deseas eliminar la moneda ${currencyToDelete?.name}? Si está en uso en facturas existentes, la acción será cancelada por seguridad.`}
        confirmText="Eliminar"
        isLoading={submitting}
      />
    </div>
  );
};
